import { QuestionAnswerDto } from "api/real-estate";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { selectReferentialDataByKey } from "features/referentials/selectors/selectReferentialDataByKey";
import { fetchSurveyByContextId } from "features/subscription-process/slices/thunks/fetchSurveyByContextId";
import { getAllReferentials } from "features/referentials/slices/getAllReferentialsThunk";
import { useCallback, useMemo, useRef } from "react"
import { useSelector } from "react-redux";
import { surveyAPI } from "services/swagger-api/surveyApi";
import { selectAllQuestions, selectQuestionsAndAnswersMappingWithEligibility } from "../slices/genericSelectors";
import { setAnswer } from "../slices/genericProfilingSlice";
import { PossibleAnswer, Question } from "../types/interfaces";
import { isIconAnswer, isInputAnswer, isInputWithUnitAnswer, isLabelAnswer } from "../types/possibleAnswer";
import { ReferentialKey } from "api/referential";
import { ProfilingMode } from "../slices/types/enums";

export enum ContextCode {
  RealEstate = "BIEN",
  BorrowerPerson = "PERSONNE",
  Unemployment = "PERSONNE",
  Coproperty = "COPRO",
}

export enum SurveyName {
  RealEstate = "Questionnaire bien CBNO",
  BorrowerPerson = "Questionnaire Situation Assuré Emprunteur",
  Unemployment = "Questionnaire Situation Assuré Chômage",
  Coproperty = "Questionnaire copropriété MRI"
}

// Stable backend codes (Codes.Questionnaires). When a code is known here,
// the survey is resolved by code, with the display name kept as fallback.
export const SURVEY_CODES: Partial<Record<SurveyName, string>> = {
  [SurveyName.Coproperty]: "COPRO_MRI"
}

interface FormValues<T = any> {
  surveyForm?: Record<string, T>;
  [key: string]: any;
}

export const useGenericSurvey = () => {
  const questionsContexts = useSelector(selectReferentialDataByKey(ReferentialKey.QuestionsContexts));
  const { answers, questionsByGroup } = useAppSelector(selectQuestionsAndAnswersMappingWithEligibility)

  const { eligibilityMessages } = useAppSelector(selectQuestionsAndAnswersMappingWithEligibility)
  const dispatch = useAppDispatch()
  const questions = useAppSelector(selectAllQuestions);

  const getSurvey = useCallback(async (contextCode: ContextCode, surveyName: SurveyName) => {
    const context = questionsContexts.find(context => context.code === contextCode);
    if (!context) {
      throw new Error(`Context ${contextCode} not found`);
    }
    const response = await surveyAPI.getSurveys({ contextId: context.id });
    // Resolve by stable code when available, fall back to the display name
    const surveyCode = SURVEY_CODES[surveyName];
    const survey = (surveyCode ? response.items?.find(item => item.code === surveyCode) : undefined)
      ?? response.items?.find(item => item.name === surveyName);
    if (!survey?.id) {
      throw new Error(`Survey ${surveyName} not found`);
    }
    return survey
  }, [questionsContexts])

  // Les référentiels sont persistés dans le navigateur : une session ancienne peut ignorer un
  // contexte ajouté depuis (COPRO). On les recharge une fois avant de conclure à son absence,
  // sinon le questionnaire précédent resterait affiché sans rien charger.
  const referentialsRefreshed = useRef(false);

  const initSurvey = useCallback((contextCode: ContextCode, surveyName: SurveyName, profilingMode: ProfilingMode) => {
    const context = questionsContexts.find(context => context.code === contextCode);
    if (context?.id) {
      dispatch(fetchSurveyByContextId({
        surveyName,
        surveyCode: SURVEY_CODES[surveyName],
        request: { contextId: context.id, activeOnly: true },
        profilingMode
      }));
    } else if (!referentialsRefreshed.current) {
      referentialsRefreshed.current = true;
      dispatch(getAllReferentials());
    } else {
      console.error(`Contexte de questionnaire ${contextCode} introuvable dans les référentiels`);
    }
  }, [dispatch, questionsContexts])

  const mapFromSurveyFormValue = useCallback((
    formValues: Record<string, PossibleAnswer[]>
  ): QuestionAnswerDto[] => {
    const questionMap = new Map<string, Question>();
    questions.forEach(question => {
      if (question.key) {
        questionMap.set(question.key, question);
      }
    });

    const answers: QuestionAnswerDto[] = [];

    Object.entries(formValues).forEach(([questionKey, answerValues]) => {
      const question = questionMap.get(questionKey);
      if (!question || !question.id) return;

      answerValues.forEach(answerValue => {
        if (question.type === 'boolean') {
          // Pour les questions booléennes
          answers.push({
            questionId: question.id!,
            answerText: String(answerValue),
            possibleAnswerId: undefined
          });
        } else {
          // Pour les questions avec possibleAnswers (label/icon)
          const possibleAnswer = question.possibleAnswers.find(pa => {
            if (isLabelAnswer(pa) || isIconAnswer(pa)) {
              // Chercher par id ou par key
              return pa.id === answerValue || pa.key === answerValue;
            }
            return false;
          });

          if (possibleAnswer && (isLabelAnswer(possibleAnswer) || isIconAnswer(possibleAnswer))) {
            answers.push({
              questionId: question.id!,
              possibleAnswerId: possibleAnswer.id,
              answerText: possibleAnswer.key as string
            });
          }
        }
      });
    });

    return answers;
  }, [questions]);

  const mapToSurveyFormValue = useCallback((answers: QuestionAnswerDto[]): Record<string, PossibleAnswer> | undefined => {
    const questionMap = new Map<string, Question>();
    questions.forEach(question => {
      if (question.id) {
        questionMap.set(question.id, question);
      }
    });
    const result: Record<string, any> = {};
    answers.forEach(answer => {
      const question = questionMap.get(answer.questionId);
      if (question) {
        let answerValue
        let possibleAnswersToSet: PossibleAnswer[] = []
        question.possibleAnswers.forEach((pa) => {
          if (isLabelAnswer(pa) || isIconAnswer(pa)) {
            if (pa.id === answer.possibleAnswerId) {
              answerValue = pa
              possibleAnswersToSet.push(pa)
            }
            if (pa.key === answer.answerText) {
              answerValue = pa.key
              possibleAnswersToSet.push(pa.key)
            }
          }
        })
        if (question.key && possibleAnswersToSet.length > 0)
          dispatch(setAnswer({ questionKey: question.key, value: possibleAnswersToSet }))
        if (question.type === 'boolean') {
          if (answer.answerText)
            answerValue = answer.answerText === "true"
          dispatch(setAnswer({ questionKey: question.key, value: [answer.answerText === "true"] }))
        }
        result[question.key] = [answerValue];
      }
    });

    return Object.keys(result).length === 0 ? undefined : result;
  }, [dispatch, questions])


  const questionWithAnswers = useCallback((surveyAnswers?: Record<string, PossibleAnswer>) => {
    if (surveyAnswers) {
      return questionsByGroup
        .map(group => ({
          title: group.title,
          questions: group.questions
            .filter(question => surveyAnswers[question.key] !== undefined) // Ne garder que les questions qui ont des réponses
            .map(question => {
              const answerValue = surveyAnswers[question.key];
              let processedAnswers;

              // Gérer les différents types de réponses
              if (Array.isArray(answerValue)) {
                // Si c'est déjà un tableau
                processedAnswers = answerValue.map((answer: any) => {
                  if (isLabelAnswer(answer) || isIconAnswer(answer)) {
                    return answer.label
                  }
                  return answer
                });
              } else if (typeof answerValue === 'boolean') {
                // Si c'est un boolean
                processedAnswers = [answerValue ? 'Oui' : 'Non'];
              } else if (typeof answerValue === 'object' && !isInputAnswer(answerValue) && !isInputWithUnitAnswer(answerValue) && answerValue.label) {
                // Si c'est un objet avec une propriété label
                processedAnswers = [answerValue.label];
              } else {
                // Pour les autres types (string, number, etc.)
                processedAnswers = [answers[answerValue as string | number] || answerValue];
              }

              return {
                key: question.key,
                question: question.label,
                answers: processedAnswers,
                eligibilityMessages: question.eligibilityMessages || {}
              };
            })
        }))
        .filter(group => group.questions.length > 0); // Ne garder que les groupes qui ont des questions avec réponses
    }
    return undefined;
  }, [answers, questionsByGroup]);

  const hasIneligibleQuestions = useCallback((surveyAnswers?: Record<string, PossibleAnswer>): boolean => {
    if (!surveyAnswers) {
      return false;
    }

    // Helper function to check if an object has a 'key' property
    const hasKeyProperty = (obj: unknown): obj is { key: unknown } => {
      return typeof obj === 'object' && obj !== null && 'key' in obj;
    };

    // Parcourir toutes les questions avec réponses
    for (const questionKey of Object.keys(surveyAnswers)) {
      const questionAnswer = surveyAnswers[questionKey];

      // Vérifier si cette question a des messages d'éligibilité
      if (eligibilityMessages[questionKey]) {
        // Extraire la clé de la réponse
        let answerKey: string;

        if (Array.isArray(questionAnswer)) {
          const firstAnswer = questionAnswer[0];
          if (hasKeyProperty(firstAnswer)) {
            answerKey = String(firstAnswer.key);
          } else {
            answerKey = String(firstAnswer);
          }
        } else if (hasKeyProperty(questionAnswer)) {
          answerKey = String(questionAnswer.key);
        } else {
          answerKey = String(questionAnswer);
        }

        // Si un message d'éligibilité existe pour cette réponse, c'est inéligible
        if (eligibilityMessages[questionKey][answerKey]) {
          return true;
        }
      }
    }
    return false;
  }, [eligibilityMessages]);

  const keyToIdMapping = useMemo(() => {
    const mapping: Record<string, string> = {};
    questions.forEach(question => {
      if (question.key && question.id) {
        mapping[question.key] = question.id;
      }
    });
    return mapping;
  }, [questions]);

  const transformFormValues = useCallback(<T extends FormValues>(
    values: T
  ): T => {
    const transformedValues = { ...values };
    if (transformedValues.surveyForm) {
      const transformedSurveyForm: Record<string, any> = {};
      Object.keys(transformedValues.surveyForm).forEach(key => {
        if (keyToIdMapping[key]) {
          const questionId = keyToIdMapping[key];
          if (transformedValues.surveyForm) {
            transformedSurveyForm[questionId] = transformedValues.surveyForm[key];
          }
        } else {
          if (transformedValues.surveyForm) {
            transformedSurveyForm[key] = transformedValues.surveyForm[key];
          }
        }
      });
      transformedValues.surveyForm = transformedSurveyForm;
    }
    return transformedValues;
  }, [keyToIdMapping]);

  return {
    getSurvey,
    initSurvey,
    mapToSurveyFormValue,
    questionWithAnswers,
    hasIneligibleQuestions,
    transformFormValues,
    mapFromSurveyFormValue
  }
}
